import type { StanDataColumn } from '@/components/core-components/StanDataRow/StanDataRow.types';
import type { Automation } from '../types/automation.types';
import type { AutomationAnalyticsData } from './analytics.logic';
import { StanBadge } from '@/components';
import { formatDateShort } from '@/lib/date';

/**
 * Build columns for automation data row
 * Returns array of columns for StanDataRow component
 */
export function buildAutomationColumns(
    automation: Automation,
    analyticsData: AutomationAnalyticsData,
    isMobile: boolean
): StanDataColumn[] {
    const isUnsaved = automation.automation_id === undefined;
    const keywords = isUnsaved ? 'New Auto-Reply' : automation.data?.trigger?.data?.keywords || 'New Auto-Reply';
    const status = automation.status === 'published' ? 'active' : 'draft';

    // Format published date - only show for published automations
    const publishedDate =
        automation.created_at && !isUnsaved && automation.status === 'published'
            ? `Published ${formatDateShort(automation.created_at)}`
            : undefined;

    // Mobile view - single column with all info
    if (isMobile) {
        return [
            {
                id: 'keywords',
                name: 'Keywords',
                value: (
                    <div className='flex min-w-0 flex-1 flex-col gap-3 pr-1.5'>
                        <div className='flex flex-col gap-1'>
                            <div className='flex items-start gap-1.5'>
                                <p className='text-label-md-bold line-clamp-2 truncate leading-tight'>{keywords}</p>
                                {!isUnsaved && (
                                    <StanBadge
                                        variant={status === 'active' ? 'success' : 'muted'}
                                        size='sm'
                                        className='shrink-0'
                                    >
                                        {status === 'active' ? 'Active' : 'Draft'}
                                    </StanBadge>
                                )}
                            </div>
                            {publishedDate && <span className='text-label-xs-regular text-gray'>{publishedDate}</span>}
                        </div>

                        <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Sent:</span>
                                <span className='text-label-sm-bold'>{analyticsData.sent.toLocaleString()}</span>
                            </div>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Clicks:</span>
                                <span className='text-label-sm-bold'>{analyticsData.clicks.toLocaleString()}</span>
                            </div>
                            <div className='flex items-center gap-1.5'>
                                <span className='text-label-xs-regular'>Purchases:</span>
                                <span className='text-label-sm-bold'>{analyticsData.purchases.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                ),
            },
        ];
    }

    // Desktop view - separate columns
    return [
        {
            id: 'keywords',
            name: 'Keywords',
            value: (
                <div className='flex w-full flex-col gap-1'>
                    <div className='flex items-center gap-2'>
                        <p className='text-label-md-bold xl:text-label-lg-bold max-w-full truncate'>{keywords}</p>
                        {!isUnsaved && (
                            <StanBadge
                                variant={status === 'active' ? 'success' : 'muted'}
                                size='sm'
                                className='shrink-0'
                            >
                                {status === 'active' ? 'Active' : 'Draft'}
                            </StanBadge>
                        )}
                    </div>
                    {publishedDate && <span className='text-label-xs-regular text-gray'>{publishedDate}</span>}
                </div>
            ),
        },
        {
            id: 'sent',
            name: 'Sent',
            value: <span className='font-semibold'>{analyticsData.sent.toLocaleString()}</span>,
        },
        {
            id: 'clicks',
            name: 'Clicks',
            value: (
                <span className='font-semibold'>
                    {analyticsData.sent > 0 && analyticsData.clicks > 0 ? analyticsData.clicks.toLocaleString() : '-'}
                </span>
            ),
        },
        {
            id: 'opt-ins',
            name: 'Opt-Ins',
            value: (
                <span className='font-semibold'>
                    {analyticsData.sent > 0 && analyticsData.purchases > 0
                        ? analyticsData.purchases.toLocaleString()
                        : '-'}
                </span>
            ),
        },
    ];
}

/**
 * Get grid template for automation row
 */
export function getGridTemplate(): string {
    return '2fr 1fr 1fr 1fr';
}
